import React, { useState } from 'react';
import { 
  BrowserRouter as Router, 
  Routes, 
  Route,
  useLocation,
  useNavigate
} from 'react-router-dom';
import { 
  Box,
  CssBaseline,
  Drawer,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ThemeProvider
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  Description as DescriptionIcon,
  CreateNewFolder as CreateIcon,
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  Article as ArticleIcon,
  PictureAsPdf as PictureAsPdfIcon
} from '@mui/icons-material';

// 导入主题
import theme from './theme/theme';

// 导入页面组件
import {
  WordPDFCreator,
  PDFFileManager,
  WordFileManager,
  WordTemplateList,
  WordTemplateCreator,
  TemplateDetail,
  ErrorBoundary
} from './components';

const drawerWidth = 260;

const menuItems = [
  { 
    text: '創建PDF', 
    path: '/create-pdf', 
    icon: <CreateIcon />,
    color: '#4CAF50' // 綠色 - 代表創建/新增
  },
  { 
    text: 'PDF文件列表', 
    path: '/pdf-files', 
    icon: <DescriptionIcon />,
    color: '#F44336' // 紅色 - PDF文檔
  },
  { 
    text: 'Word文件列表', 
    path: '/word-files', 
    icon: <ArticleIcon />,
    color: '#2196F3' // 藍色 - Word文檔
  },
  { 
    text: '模板列表', 
    path: '/template-list', 
    icon: <FolderIcon />,
    color: '#FF9800' // 橙色 - 文件夾/模板
  },
  { 
    text: '創建模板', 
    path: '/create-template', 
    icon: <FileIcon />,
    color: '#9C27B0' // 紫色 - 創建模板
  }
];

const AppContent: React.FC = () => {
  const [open, setOpen] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: 'background.default' }}>
      <CssBaseline />
      
      <Drawer
        variant="permanent"
        open={open}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          whiteSpace: 'nowrap',
          boxSizing: 'border-box',
          ...(open && {
            width: drawerWidth,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              transition: (theme) => theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen
              }),
              overflowX: 'hidden',
              backgroundColor: '#ffffff',
              borderRight: '1px solid #e0e0e0'
            }
          }),
          ...(!open && {
            '& .MuiDrawer-paper': {
              transition: (theme) => theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen
              }),
              overflowX: 'hidden',
              width: (theme) => theme.spacing(7),
              backgroundColor: '#ffffff',
              borderRight: '1px solid #e0e0e0'
            }
          })
        }}
      >
        {/* Logo and Title Section */}
        <Box sx={{ 
          p: 3, 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}>
          <Box sx={{ 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 40,
            height: 40,
            borderRadius: 2,
            bgcolor: 'primary.main',
            flexShrink: 0
          }}>
            <PictureAsPdfIcon sx={{ color: 'white', fontSize: 24 }} />
          </Box>
          {open && (
            <Typography 
              variant="h6" 
              noWrap 
              sx={{ 
                fontWeight: 700,
                color: 'primary.main',
                letterSpacing: 0.5
              }}
            >
              PDF Maker
            </Typography>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <IconButton
            onClick={handleDrawerToggle}
            size="small"
            sx={{ 
              color: 'text.secondary',
              transform: open ? 'rotate(0deg)' : 'rotate(180deg)',
              transition: 'transform 0.3s ease-in-out',
              '&:hover': {
                bgcolor: 'action.hover'
              }
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
        </Box>

        {/* Menu Items */}
        <Box sx={{ overflow: 'auto', py: 2, flexGrow: 1 }}>
          <List sx={{ px: 1 }}>
            {menuItems.map((item) => (
              <ListItem key={item.text} disablePadding sx={{ display: 'block', mb: 0.5 }}>
                <ListItemButton
                  selected={location.pathname === item.path}
                  onClick={() => handleNavigate(item.path)}
                  sx={{
                    minHeight: 48,
                    justifyContent: open ? 'initial' : 'center',
                    px: 2.5,
                    borderRadius: 2,
                    transition: 'all 0.2s',
                    '&.Mui-selected': {
                      backgroundColor: 'primary.50',
                      color: 'primary.main',
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: 'primary.100'
                      }
                    },
                    '&:hover': {
                      backgroundColor: 'grey.50'
                    }
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : 'auto',
                      justifyContent: 'center',
                      color: item.color,
                      '& .MuiSvgIcon-root': {
                        fontSize: 24,
                        transition: 'all 0.2s ease-in-out'
                      },
                      ...(location.pathname === item.path && {
                        '& .MuiSvgIcon-root': {
                          transform: 'scale(1.1)',
                          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
                        }
                      })
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.text} 
                    sx={{ 
                      opacity: open ? 1 : 0,
                      '& .MuiTypography-root': {
                        fontWeight: location.pathname === item.path ? 600 : 400
                      }
                    }} 
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
        
        <Box sx={{ flexGrow: 1 }} />
        <Divider />
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography 
            variant="caption" 
            sx={{ 
              opacity: open ? 1 : 0,
              color: 'text.secondary'
            }}
          >
            © ajo developer
          </Typography>
        </Box>
      </Drawer>
      
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          backgroundColor: 'background.default',
          overflow: 'auto',
          height: '100vh'
        }}
      >
        <Routes>
          <Route path="/create-pdf" element={<WordPDFCreator />} />
          <Route path="/pdf-files" element={<PDFFileManager />} />
          <Route path="/word-files" element={<WordFileManager />} />
          <Route path="/template-list" element={<WordTemplateList />} />
          <Route path="/create-template" element={<WordTemplateCreator />} />
          <Route path="/templates/edit/:templateId" element={<TemplateDetail />} />
          <Route path="/" element={<WordPDFCreator />} />
          <Route path="*" element={<Typography variant="h4">404 Not Found</Typography>} />
        </Routes>
      </Box>
    </Box>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <Router>
        <ErrorBoundary>
          <AppContent />
        </ErrorBoundary>
      </Router>
    </ThemeProvider>
  );
};

export default App;